import GeckoStore from '../gecko-store';
import { reduxStore } from '../gecko-store/redux';
import { GeckoSoundAlert } from 'ui';
import io from 'socket.io-client';
import TitleNotifications from 'services/title-notifications';
import FailedToReconnectSockets from './Exceptions/FailedToReconnectSockets';
import GeckoAuth from '../gecko-auth';
import GeckoEvents from '../gecko-events';
import { fetchConversationCounts } from 'routes/conversations/actions';

let connectionErrorCount = 0;

let timer;
// Should try to reconnect this many times before reporting
const MINIMUM_SOCKET_CONNECT_ATTEMPTS = 5;

const debounce = (func, delay = 2000) => {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

let socket;
let authenticating = false;
let initialised = false;
const init = () => {
  if (initialised === true) {
    return;
  }
  const apiUrl = GeckoStore.redux.get('appState.apiUrl');
  initialised = true;
  authenticating = true;

  socket = io(apiUrl);

  socket.on('connect', () => {
    console.log('GeckoSocket: socket connected');
    GeckoAuth.getToken().then((token) => {
      socket.emit('authenticate', { token });
    });
  });
  socket.on('authenticated', () => {
    console.log('GeckoSocket: socket authenticated');
    GeckoEvents.resetup();
    require('services/typing-indicator');
    authenticating = false;

    const models = [
      'agent',
      'message',
      'bot_tag',
      'channel',
      'field',
      'note',
      'field_mapping',
      'agent_team',
      'saved_reply',
      'skill',
      'team',
      'tag',
      'workflow',
      'feature',
    ];
    models.forEach((model) => {
      const postfix = model.toUpperCase();
      socket.on(`${model}:created`, (data) =>
        GeckoStore.redux.dispatch({ type: `NEW_${postfix}`, payload: data })
      );
      socket.on(`${model}:updated`, (data) =>
        GeckoStore.redux.dispatch({ type: `UPDATE_${postfix}`, payload: data })
      );
      socket.on(`${model}:deleted`, (data) =>
        GeckoStore.redux.dispatch({ type: `DELETE_${postfix}`, payload: data })
      );
    });
    socket.on('conversation:deleted', (data) =>
      GeckoStore.redux.dispatch({ type: 'DELETE_CONVERSATION', payload: data })
    );
    socket.on('contact:deleted', (data) =>
      GeckoStore.redux.dispatch({ type: 'DELETE_CONTACT', payload: data })
    );

    const playConversationAlert = (conversation) => {
      if (conversation.status === 'new') {
        return new GeckoSoundAlert().newConvo();
      }
      return new GeckoSoundAlert().receive();
    };

    const notifyUser = (conversation, contact) => {
      // Change the Title based on if we know the contact
      if (contact) {
        TitleNotifications.set(
          `${contact.first_name || contact.additional.fake_first_name} says...`
        );
      } else {
        TitleNotifications.set('New message');
      }

      const playSoundWithTimeout = debounce(() =>
        playConversationAlert(conversation)
      );

      playSoundWithTimeout();
    };

    socket.on('conversation:updated', (conversation) => {
      const state = reduxStore.getState();
      // If the Convesration doesn't have unread messages we can quit early
      if (conversation.unread_count < 1) {
        return;
      }
      // If an agent sent the last msesage we can quit early
      const isLastMessageFromAgent = state.agents.some(
        (agent) => Number(agent.id) === Number(conversation.last_message_from)
      );

      const bot = conversation.bot_id
        ? state.botsRoute.botsById[conversation.bot_id]
        : null;

      const isLastMessageFromBot = bot
        ? Number(bot.user_id) === Number(conversation.last_message_from)
        : false;

      const contact =
        state.conversationsRoute.contactsById[conversation.last_message_from];
      const ILastMessageFromSystemUser =
        contact?.last_name === 'Bot' && contact?.type === 'system';

      if (
        isLastMessageFromAgent ||
        isLastMessageFromBot ||
        ILastMessageFromSystemUser
      ) {
        return;
      }

      const user = state.appState.loggedInUser;
      const userTeams = state.appState.myTeamIds;

      const userListensFor = {
        bot: user.additional['desktop_notify_bot'] || false,
        unassigned: user.additional['desktop_notify_unassigned'] || false,
        team: user.additional['desktop_notify_team'] || false,
        assigned: user.additional['desktop_notify_mine'] || false,
        other: user.additional['desktop_notify_other'] || false,
      };

      const userIsIndirectlyAssigned =
        conversation.all_assigned_agent_ids.length &&
        conversation.all_assigned_agent_ids.includes(user.id);
      const userIsDirectlyAssigned =
        conversation.assigned_agent_ids.length &&
        conversation.assigned_agent_ids.includes(user.id);
      const isUnassigned = !conversation.all_assigned_agent_ids.length;
      const isTeamAssigned =
        conversation.assigned_team_ids.length &&
        userTeams.length &&
        conversation.assigned_team_ids.some((teamId) =>
          userTeams.includes(teamId)
        );

      // Bot
      if (conversation.bot_id && userListensFor.bot) {
        return notifyUser(conversation, contact);
      }
      // Direct Assigned
      if (userIsDirectlyAssigned && userListensFor.assigned) {
        return notifyUser(conversation, contact);
      }
      // Other Assigned
      if (!isUnassigned && !userIsIndirectlyAssigned && userListensFor.other) {
        return notifyUser(conversation, contact);
      }
      // Team Assigned
      if (isTeamAssigned && userListensFor.team) {
        return notifyUser(conversation, contact);
      }
      // Unassigned
      if (isUnassigned && !conversation.bot_id && userListensFor.unassigned) {
        return notifyUser(conversation, contact);
      }
    });

    socket.on('unassigned-from-conversation', (unassignment) => {
      const agent = GeckoStore.redux.get('appState.loggedInUser');
      if (agent.permissions.restrict_conversations) {
        return GeckoStore.redux.dispatch({
          type: 'RESTRICTED_CONVERSATION_UNASSIGNED_FROM_ME',
          payload: unassignment.conversation_id,
        });
      }
      GeckoStore.redux.dispatch({
        type: 'CONVERSATION_UNASSIGNED_FROM_ME',
        payload: { id: unassignment.conversation_id },
      });
    });

    socket.on('note:created', (note) => {
      if (note.notable !== 'user') {
        return;
      }
      GeckoStore.redux.dispatch({
        type: 'NEW_CONTACT_NOTE',
        payload: { contactId: note.notable_id, note },
      });
    });
    socket.on('note:updated', (note) => {
      if (note.notable !== 'user') {
        return;
      }
      GeckoStore.redux.dispatch({
        type: 'UPDATED_CONTACT_NOTE',
        payload: { contactId: note.notable_id, note },
      });
    });
    socket.on('note:deleted', (note) => {
      if (note.notable !== 'user') {
        return;
      }
      GeckoStore.redux.dispatch({
        type: 'DELETED_CONTACT_NOTE',
        payload: { contactId: note.notable_id, note },
      });
    });

    fetchConversationCounts();
  });
  socket.on('unauthorized', () => {
    authenticating = false;
    console.log('GeckoSocket: socket authentication failed');
    console.log('GeckoSocket: socket disconnected');
    setTimeout(() => init(), 5000);
  });

  socket.on('activity:created', (activity) => {
    const state = reduxStore.getState();
    const userId = state.appState.loggedInUser.id;
    if (!activity.metadata.log_type) {
      return;
    }
    if (userId !== activity.subject_id) {
      return;
    }
    GeckoStore.redux.dispatch({
      type: 'NEW_AVAILABILITY_ACTIVITY',
      payload: {
        agent: activity.metadata.agent,
        admin: activity.metadata.admin,
        createdAt: activity.createdAt,
      },
    });
  });
  socket.on('connect', () => {
    connectionErrorCount = 0;
  });

  socket.on('reconnecting', () => {
    console.log('reconnecting');

    if (connectionErrorCount === MINIMUM_SOCKET_CONNECT_ATTEMPTS) {
      const user = GeckoStore.redux.get('appState.loggedInUser');
      try {
        if (user) {
          window.Raven.setUserContext({
            email: user.email,
            id: user.id,
          });
        }
        window.Raven.captureException(
          new FailedToReconnectSockets(connectionErrorCount)
        );
      } catch (error) {
        console.error(error);
      }
    }

    connectionErrorCount++;
  });
};

const GeckoSocket = {
  on(...args) {
    if ((!socket || !socket.connected) && !authenticating) {
      init();
    }
    return socket.on(...args);
  },
  emit(...args) {
    if ((!socket || !socket.connected) && !authenticating) {
      init();
    }
    return socket.emit(...args);
  },
  removeListener(...args) {
    if ((!socket || !socket.connected) && !authenticating) {
      init();
    }
    return socket.removeListener(...args);
  },
  init: init,
};

export default GeckoSocket;
